import {SpanIcon} from '@mazraeapp/react_icon_kit';
import {useField, useFormikContext} from 'formik';
import {useCallback, useEffect, useState} from 'react';
import {BottomSheet} from 'react-spring-bottom-sheet';
import Button from '../../../../../../components/button';
import DatePickerBottomSheetModal from '../../../../../../components/DatePickerBottomSheet/DatePickerBottomSheetModal';
import SelectOutlet from '../../../../../../components/selectOutlet';
import {
  CitiesToOption,
  ProvincesToOption,
} from '../../../../../../utilities/helper/provincesToOption';
import {toEnglishDigits} from '../../../../../../utilities/helper/toEnglishNumber';
import UseGetProvince from '../../../../../../utilities/hooks/province/useGetProvince';

const ADDRESS_FIELD_NAME = 'store.address';
const ADDRESS_SEPARATOR = '، ';

type Props = {
  title: string;
  name: string;
  isLast?: boolean;
  isRequired?: boolean;
  fieldType?: 'text' | 'date' | 'number';
};

export const EditableFieldBottomSheet = ({
  title,
  name,
  isLast,
  isRequired = false,
  fieldType = 'text',
}: Props) => {
  const [, meta, helpers] = useField(name);
  const {setFieldValue, validateField} = useFormikContext<any>();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const {provinces} = UseGetProvince();
  const isAddressField = name === ADDRESS_FIELD_NAME;

  // useEffect to fill the state of keyboardOpen
  useEffect(() => {
    const vv = window.visualViewport;

    if (!vv) return;

    const handler = () => {
      // if viewport height shrinks → keyboard likely open
      const isKeyboard = window.innerHeight - vv.height > 120;
      setKeyboardOpen(isKeyboard);
    };

    vv.addEventListener('resize', handler);

    return () => vv.removeEventListener('resize', handler);
  }, []);

  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState('');
  const [tempState, setTempState] = useState('');
  const [tempCity, setTempCity] = useState('');

  const currentValue = meta.value ?? '';
  const error = meta.touched && meta.error ? meta.error : undefined;

  const findDefaultCityById = useCallback(
    (value: string) => {
      const province = provinces?.find(state => state.id === +value);
      return province?.cities?.[0]?.id?.toString() || '';
    },
    [provinces],
  );

  const parseAddressSelection = useCallback(
    (address: string) => {
      if (!address || !provinces?.length) {
        return {state: '', city: '', detail: address || ''};
      }

      const province = provinces.find(item => address.includes(item.name));
      const city = province?.cities?.find(item => address.includes(item.name));

      let detail = address;
      if (province?.name) {
        detail = detail.replace(province.name, '');
      }
      if (city?.name) {
        detail = detail.replace(city.name, '');
      }
      detail = detail
        .split(ADDRESS_SEPARATOR)
        .map(part => part.trim())
        .filter(Boolean)
        .join(ADDRESS_SEPARATOR);

      return {
        state: province?.id?.toString() || '',
        city: city?.id?.toString() || '',
        detail,
      };
    },
    [provinces],
  );

  const joinAddressSelection = (
    stateId: string,
    cityId: string,
    detail: string,
  ) => {
    const province = provinces?.find(item => item.id === Number(stateId));
    const city = province?.cities?.find(item => item.id === Number(cityId));

    return [province?.name, city?.name, detail.trim()]
      .filter(Boolean)
      .join(ADDRESS_SEPARATOR);
  };

  const handleOpen = () => {
    if (isAddressField) {
      const selection = parseAddressSelection(currentValue || '');
      const nextState = selection.state || provinces?.[0]?.id?.toString() || '';
      setTempState(nextState);
      setTempCity(selection.city || findDefaultCityById(nextState));
      setTempValue(selection.detail);
    } else {
      setTempValue(currentValue || '');
    }
    setOpen(true);
  };

  const handleSave = async () => {
    const nextValue = isAddressField
      ? joinAddressSelection(tempState, tempCity, tempValue)
      : tempValue;

    await setFieldValue(name, nextValue, true);
    helpers.setTouched(true, true);
    setOpen(false);
  };
  // Handle number input change - only allow digits
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Convert Persian/Arabic digits to English, then remove all non-digit characters
    const cleaned = toEnglishDigits(value).replace(/\D/g, '');
    setTempValue(cleaned);
  };

  // Handle text input change
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(e.target.value);
  };

  const isSaveButtonDisabled = isAddressField
    ? isRequired && (!tempState || !tempCity || !tempValue.trim())
    : isRequired && !tempValue.trim();

  // Date Fields
  if (fieldType === 'date') {
    return (
      <>
        <div
          className={`flex items-center justify-center py-2 text-sm ${
            !isLast && 'border-b'
          } cursor-pointer`}
          onClick={handleOpen}
        >
          <span className="w-1/2 text-gray-500">{title}</span>
          <span className="w-1/2 text-gray-800 break-words">
            {currentValue || '-'}
          </span>
          <i className="px-2 text-gray-600 icon-nameEdit" />
        </div>
        {error && <div className="my-2 ml-2 text-sm text-red-600">{error}</div>}

        <DatePickerBottomSheetModal
          title={title}
          value={currentValue}
          isOpen={open}
          onClose={() => setOpen(false)}
          onChange={value => {
            setFieldValue(name, value, false);
            helpers.setTouched(true, false);
            validateField(name);
          }}
        />
      </>
    );
  }

  if (fieldType === 'number') {
    return (
      <>
        <div
          className={`flex items-center justify-center py-2 text-sm ${
            !isLast && 'border-b'
          } cursor-pointer`}
          onClick={handleOpen}
        >
          <span className="w-1/2 text-gray-500">{title}</span>
          <span className="w-1/2 text-gray-800 break-words">
            {currentValue || '-'}
          </span>
          <i className="px-2 text-gray-600 icon-nameEdit" />
        </div>

        {error && <div className="my-2 ml-2 text-sm text-red-600">{error}</div>}

        <BottomSheet
          open={open}
          onDismiss={() => setOpen(false)}
          blocking
          className="custom-bottom-sheet"
        >
          <div
            className={`relative flex max-h-[85vh] flex-col gap-1.5 overflow-y-auto p-4 pb-44 ${
              keyboardOpen ? 'mb-20' : 'mb-0'
            }`}
          >
            <SpanIcon
              className="absolute top-4 left-5 text-2xl text-gray-600 cursor-pointer icon-nameClose-remove"
              onClick={() => setOpen(false)}
            />

            <h4 className="font-medium text-center text-md text-zinc-800">
              ویرایش اطلاعات
            </h4>

            <h5 className="text-sm text-zinc-500">
              {title}{' '}
              {isRequired && <span className="mr-1 text-red-700">*</span>}
            </h5>

            <input
              tabIndex={-1}
              type="tel"
              inputMode="numeric"
              value={tempValue}
              onChange={handleNumberChange}
              placeholder={`${title} را وارد کنید`}
              className="px-3 mb-2 w-full h-10 text-sm text-right rounded-lg border border-gray-500 ring-gray-400 appearance-none outline-none focus:ring-0"
            />

            <Button
              baseStyles="flex flex-1 items-center justify-center rounded-lg font-medium transition-colors outline-none text-md min-h-10 disabled:opacity-50 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
              type="button"
              disabled={isSaveButtonDisabled}
              onClick={handleSave}
            >
              ثبت
            </Button>
          </div>
        </BottomSheet>
      </>
    );
  }

  if (isAddressField) {
    return (
      <>
        <div
          className={`flex items-center justify-center py-2 text-sm ${
            !isLast && 'border-b'
          } cursor-pointer`}
          onClick={handleOpen}
        >
          <span className="w-1/2 text-gray-500">{title}</span>
          <span className="w-1/2 text-gray-800 break-words">
            {currentValue || '-'}
          </span>
          <i className="px-2 text-gray-600 icon-nameEdit" />
        </div>

        {error && <div className="my-2 ml-2 text-sm text-red-600">{error}</div>}

        <BottomSheet
          open={open}
          onDismiss={() => setOpen(false)}
          blocking
          className="custom-bottom-sheet"
        >
          <div
            className={`relative flex max-h-[85vh] flex-col gap-3 overflow-y-auto p-4 pb-44 ${
              keyboardOpen ? 'mb-20' : 'mb-0'
            }`}
          >
            <SpanIcon
              className="absolute top-4 left-5 text-2xl text-gray-600 cursor-pointer icon-nameClose-remove"
              onClick={() => setOpen(false)}
            />
            <h4 className="font-medium text-center text-md text-zinc-800">
              ویرایش اطلاعات
            </h4>
            <h5 className="text-sm text-zinc-500">
              {title}{' '}
              {isRequired && <span className="mr-1 text-red-700">*</span>}
            </h5>

            <SelectOutlet
              hasEmptyData={false}
              name="state"
              label="استان"
              required={isRequired}
              value={tempState}
              touched={Boolean(tempState)}
              options={ProvincesToOption(provinces || [])}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const nextState = e.target.value;
                setTempState(nextState);
                setTempCity(findDefaultCityById(nextState));
              }}
            />

            <SelectOutlet
              hasEmptyData={false}
              name="city"
              label="شهرستان"
              required={isRequired}
              value={tempCity}
              touched={Boolean(tempCity)}
              options={CitiesToOption(
                provinces?.find(x => x.id === Number(tempState))?.cities || [],
              )}
              disabled={!tempState}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setTempCity(e.target.value);
              }}
            />

            <input
              tabIndex={-1}
              type="text"
              value={tempValue}
              onChange={handleTextChange}
              placeholder="آدرس را وارد کنید"
              className="px-3 mb-2 w-full h-10 text-sm rounded-lg border border-gray-500 ring-gray-400 appearance-none outline-none focus:ring-0"
            />

            <Button
              baseStyles="flex flex-1 items-center justify-center rounded-lg font-medium transition-colors outline-none text-md min-h-10 disabled:opacity-50 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
              type="button"
              disabled={isSaveButtonDisabled}
              onClick={handleSave}
            >
              ثبت
            </Button>
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <>
      <div
        className={`flex items-center justify-center py-2 text-sm ${
          !isLast && 'border-b'
        } cursor-pointer`}
        onClick={handleOpen}
      >
        <span className="w-1/2 text-gray-500">{title}</span>
        <span className="w-1/2 text-gray-800 break-words">
          {currentValue || '-'}
        </span>
        <i className="px-2 text-gray-600 icon-nameEdit" />
      </div>

      {error && <div className="my-2 ml-2 text-sm text-red-600">{error}</div>}

      <BottomSheet
        open={open}
        onDismiss={() => setOpen(false)}
        blocking
        className="custom-bottom-sheet"
      >
        <div
          className={`relative flex max-h-[85vh] flex-col gap-1.5 overflow-y-auto p-4 pb-44 ${
            keyboardOpen ? 'mb-20' : 'mb-0'
          }`}
        >
          <SpanIcon
            className="absolute top-4 left-5 text-2xl text-gray-600 cursor-pointer icon-nameClose-remove"
            onClick={() => setOpen(false)}
          />
          <h4 className="font-medium text-center text-md text-zinc-800">
            ویرایش اطلاعات
          </h4>
          <h5 className="text-sm text-zinc-500">
            {title} {isRequired && <span className="mr-1 text-red-700">*</span>}
          </h5>

          <input
            tabIndex={-1}
            type="text"
            value={tempValue}
            onChange={handleTextChange}
            placeholder={`${title} را وارد کنید`}
            className="px-3 mb-2 w-full h-10 text-sm rounded-lg border border-gray-500 ring-gray-400 appearance-none outline-none focus:ring-0"
          />

          <Button
            baseStyles="flex flex-1 items-center justify-center rounded-lg font-medium transition-colors outline-none text-md min-h-10 disabled:opacity-50 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
            type="button"
            disabled={isSaveButtonDisabled}
            onClick={handleSave}
          >
            ثبت
          </Button>
        </div>
      </BottomSheet>
    </>
  );
};

export default EditableFieldBottomSheet;
